// Web-admin permissions (permission tree keys, see backend utils/permissionTree.js).
//   page key   : "module.sub"          e.g. "merchants.commission"
//   option key : "module.sub.option"   e.g. "merchants.commission.edit"
// Super Admin (level 1) has every key. Loaded once per login from GET /permissions/me.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import AuthContext from './AuthContext';

const PermissionContext = createContext({
  loading: true,
  superAdmin: false,
  level: null,
  permissions: [],
  can: () => false,
  canAny: () => false,
  refresh: () => {},
});

export const PermissionProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [state, setState] = useState({ loading: true, superAdmin: false, level: null, permissions: [] });

  const load = useCallback(async () => {
    const authToken = token || sessionStorage.getItem('token');
    if (!authToken) {
      setState({ loading: false, superAdmin: false, level: null, permissions: [] });
      return;
    }
    try {
      const res = await axios.get(`${API_BASE_URL}/permissions/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setState({
        loading: false,
        superAdmin: !!res.data.superAdmin,
        level: res.data.level ?? null,
        permissions: Array.isArray(res.data.permissions) ? res.data.permissions : [],
      });
    } catch (err) {
      console.error('Error loading permissions:', err);
      setState({ loading: false, superAdmin: false, level: null, permissions: [] });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(() => {
    const held = new Set(state.permissions);
    const can = (key) => state.superAdmin || held.has(key);
    const canAny = (keys) => state.superAdmin || (keys || []).some((k) => held.has(k));
    return { ...state, can, canAny, refresh: load };
  }, [state, load]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

/** const { can, canAny, superAdmin, loading } = usePermissions(); */
export const usePermissions = () => useContext(PermissionContext);

/** <Can perm="merchants.commission.edit">...</Can>  or  <Can any={['a.b', 'c.d']}>...</Can> */
export const Can = ({ perm, any, fallback = null, children }) => {
  const { can, canAny } = usePermissions();
  const allowed = any ? canAny(any) : can(perm);
  return allowed ? children : fallback;
};

/** Route guard: renders the page only when the user holds one of the page keys. */
export const RequirePermission = ({ perm, any, children }) => {
  const { loading, can, canAny } = usePermissions();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  const allowed = any ? canAny(any) : can(perm);
  if (allowed) return children;
  return (
    <Box sx={{ p: 6, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Access denied</Typography>
      <Typography color="text.secondary">You do not have permission to open this page.</Typography>
    </Box>
  );
};

/** Redirect to the first page the user may open (used for "/"). */
export const FirstAllowedRedirect = ({ candidates }) => {
  const { loading, canAny } = usePermissions();
  if (loading) return null;
  const hit = candidates.find((c) => canAny(c.perms));
  return <Navigate to={hit ? hit.path : '/profile'} replace />;
};

export default PermissionContext;
